import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import FamilyMeetingMinutesForm from "./FamilyMeetingMinutesForm";
import {
    fetchFamilyMeetingMinute,
    updateFamilyMeetingMinute,
    clearError,
} from "../../../store/slices/familyMeetingMinutesSlice";

const EditFamilyMeetingMinutes = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentFamilyMeetingMinute, isLoading, error } = useSelector(
        (state) => state.familyMeetingMinutes
    );

    const [formData, setFormData] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // Fetch record
    useEffect(() => {
        dispatch(fetchFamilyMeetingMinute(id));
    }, [dispatch, id]);

    // Sync Redux → Local state
    useEffect(() => {
        if (currentFamilyMeetingMinute?.id) {
            const data = currentFamilyMeetingMinute;
            setFormData({
                patientId: patientId || data.patient_id,
                caseId: caseId || data.case_id,
                clientName: data.client_name || "",
                reportWrittenBy: data.report_written_by || "",
                date: data.date || data.meeting_date || "",
                familyMembers:
                    data.family_members?.map((m) => ({ name: m.name || "" })) || [{ name: "" }],
                teamMembers:
                    data.team_members?.map((m) => ({ name: m.name || "" })) || [{ name: "" }],
                agendaOfMeeting: data.agenda || "",
                summaryOfDiscussion: data.summary || "",
                actionPlanAgreedUpon: data.action_plan || "",
            });
        }
    }, [currentFamilyMeetingMinute]);

    // Handle submit
    const handleSubmit = async (values) => {
        const payload = {
            patient_id: values.patientId|| patientId,
            case_id: values.caseId|| caseId,
            client_name: values.clientName,
            report_written_by: values.reportWrittenBy,
            date: values.date,
            agenda: values.agendaOfMeeting,
            summary: values.summaryOfDiscussion,
            action_plan: values.actionPlanAgreedUpon,
            family_members: values.familyMembers.map((m) => ({ name: m.name })),
            team_members: values.teamMembers.map((m) => ({ name: m.name })),
        };

        const newErrors = {};
        if (!payload.client_name) newErrors.client_name = "Client name is required.";
        if (!payload.report_written_by) newErrors.report_written_by = "Report written by is required.";
        if (!payload.date) newErrors.date = "Date is required.";

        if (Object.keys(newErrors).length > 0) {
            setValidationErrors(newErrors);
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields.",
                icon: "warning",
            });
            return;
        }

        try {
            await dispatch(updateFamilyMeetingMinute({ case_id: caseId, data: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Family meeting minutes updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            console.error("Update error:", err);
            setValidationErrors(err?.errors || {});
            Swal.fire({
                title: "Error",
                text: err?.message || "Failed to update record.",
                icon: "error",
            });
        }
    };

    // Show API error as toast
    useEffect(() => {
        if (error) {
            Swal.fire({ title: "Error", text: error, icon: "error" });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    if (isLoading || !formData) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/family-meeting-minutes/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        {/* Validation Errors */}
                        {Object.keys(validationErrors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.entries(validationErrors).map(([key, value], i) => (
                                    <div key={i}>
                                        {Array.isArray(value) ? value.join(", ") : value}
                                    </div>
                                ))}
                            </div>
                        )}

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = e.target;
                                const values = form.__formData__;
                                if (values) handleSubmit(values);
                            }}
                        >
                            <FamilyMeetingMinutesForm
                                initialData={formData}
                                onChange={(data) => {
                                    const form = document.querySelector("form");
                                    if (form) form.__formData__ = data;
                                }}
                            />

                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-edit-2-line me-1"></i> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>

                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditFamilyMeetingMinutes;